import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { DisplayOptionList } from '@amfam/policy/models/lib/models/displayList';
import { GenericProductType } from '@amfam/policy/models/lib/models/generic-product-type';
import { Policy } from '@amfam/policy/models/lib/models/policy';
import { PolicyAccessBanner } from '@amfam/policy/models/lib/models/policy-access-banner';
import { PolicyMigrationBanner } from '@amfam/policy/models/lib/models/policy-migration';
import { PolicySummary } from '@amfam/policy/models/lib/models/policy-summary';
import { UserAccountPolicyAnalytics } from '@amfam/shared/analytics/lib/models/analytics.model';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import { selectMdmId } from '@amfam/shared/user/lib/+state/user.selectors';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { removeDuplicateAndExpiredPolicies } from '@amfam/shared/utility/shared-services/lib/operators/policy.operators';

import { LifeService } from './life.service';
import { PlautoService } from './plauto.service';
import { PlpropertyService } from './plproperty.service';
import { PlumbrellaService } from './plumbrella.service';
import { getPoliciesFeatureToggles } from './policies-feature-toggles';
import { TypedWorker } from './typed-worker';

export interface PolicySummaryApiResult {
  policies: PolicySummary[];
  afiMidvaleBillingAccountNumbers: string[];
  phoenixHorisonBillingAccountNumbers: string[];
  customerGroupId: string;
  customerSegmentCd: string;
  displayOptions: DisplayOptionList | null;
  ga4Analytics: UserAccountPolicyAnalytics | null;
  migrationBanner: PolicyMigrationBanner | null;
  policyAccessBanners: PolicyAccessBanner[];
}

@Injectable({
  providedIn: 'root'
})
export class PolicyService {
  private store = inject(Store);
  private http = inject(HttpClient);
  private plProperty = inject(PlpropertyService);
  private plAutoService = inject(PlautoService);
  private plUmbrellaService = inject(PlumbrellaService);
  private lifeService = inject(LifeService);
  private featureFlagService = inject(FeatureFlagService);
  private utilService = inject(UtilService);
  private applicationService = inject(ApplicationService);
  private dataDogService = inject(DataDogService);

  private readonly clPoliciesApiToggle = 'includeClFusion';

  private supportedPolicyTypes = [
    GenericProductType.Auto,
    GenericProductType.Home,
    GenericProductType.Umbrella,
    GenericProductType.Life
  ];

  public isPolicyTypeSupported(policyType: GenericProductType | undefined): boolean {
    return policyType !== undefined && this.supportedPolicyTypes.includes(policyType);
  }

  public getPolicySummaries(): Observable<PolicySummaryApiResult> {
    return this.getPolicySummariesFromUserAPI();
  }

  public getPolicySummariesFromUserAPI(): Observable<PolicySummaryApiResult> {
    // Get mdmId as Observable
    const mdmId$ = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)
      ? this.store.select(selectMdmId).pipe(take(1))
      : of(undefined);

    return mdmId$.pipe(
      switchMap(mdmId => {
        let expUrl, operatorOnlyExpUrl;
        const featureToggles = getPoliciesFeatureToggles(this.featureFlagService);

        if (this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)) {
          expUrl = `${env.userAccountApi}/policies?mdmId=${mdmId}`;
          if (featureToggles.length > 0) {
            expUrl += `&feature-toggle=${featureToggles.join(',')}`;
          }
          operatorOnlyExpUrl = `${env.userAccountApi}/operatorOnlyPolicies?mdmId=${mdmId}`;
        } else {
          expUrl = `${env.userAccountApi}/policies`;
          if (featureToggles.length > 0) {
            expUrl += `?feature-toggle=${featureToggles.join(',')}`;
          }
          operatorOnlyExpUrl = `${env.userAccountApi}/operatorOnlyPolicies`;
        }

        const headers = this.getHeaders();

        return forkJoin([
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.http.get<any>(operatorOnlyExpUrl).pipe(
            catchError(error => {
              this.dataDogService.log('Caught RxJS error in operatorOnlyExpUrl', 'error', {
                mdmId,
                error: error?.message || error
              });
              if (env.environment !== 'prod') {
                console.error('Caught RxJS error in operatorOnlyExpUrl:', error.message);
              }
              return of(null);
            })
          ),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.http.get<any>(expUrl, headers ? { headers } : {}).pipe(
            catchError(error => {
              this.dataDogService.log('Caught RxJS error in expUrl', 'error', {
                mdmId,
                error: error?.message || error
              });
              if (env.environment !== 'prod') {
                console.error('Caught RxJS error in expUrl:', error.message);
              }
              return of(null);
            })
          )
        ]).pipe(
          switchMap(([operatorProducts, expProducts]) => {
            // Extract metadata from the raw response top-level fields.
            // This must happen before the policies filter so it survives even when policies is null.
            const afiMidvaleBillingAccountNumbers =
              expProducts?.['response']?.afiMidvaleBillingAccountNumbers ?? [];
            const phoenixHorisonBillingAccountNumbers =
              expProducts?.['response']?.phoenixHorisonBillingAccountNumbers ?? [];
            const customerGroupId = expProducts?.['response']?.customerGroupId ?? '';
            const customerSegmentCd = expProducts?.['response']?.customerSegmentCd ?? '';
            const displayOptions = expProducts?.['response']?.displayOptions ?? null;
            const ga4Analytics = expProducts?.['response']?.ga4Analytics ?? null;

            // Kept out of `metadata` so it isn't copied onto every policy.
            const migrationBanner: PolicyMigrationBanner | null =
              expProducts?.['response']?.migrationBanner ?? null;
            const policyAccessBanners: PolicyAccessBanner[] =
              operatorProducts?.['response']?.banners ?? [];

            const metadata = {
              afiMidvaleBillingAccountNumbers,
              phoenixHorisonBillingAccountNumbers,
              customerGroupId,
              customerSegmentCd,
              displayOptions,
              ga4Analytics
            };

            const operatorPrds = operatorProducts?.['response']?.['policies'] ?? [];
            const expPrds = expProducts?.['response']?.['policies'] ?? [];

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let rawPolicies: any[];
            if (operatorPrds.length > 0) {
              rawPolicies = [
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...expPrds.map((exp: any) => ({
                  ...exp,
                  customerId: expProducts?.['response']?.customerId,
                  ...metadata
                })),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...operatorPrds.map((op: any) => ({
                  ...op,
                  operator: true,
                  customerId: operatorProducts?.['response']?.customerId,
                  customerGroupId: operatorProducts?.['response']?.customerGroupId,
                  customerSegmentCd: operatorProducts?.['response']?.customerSegmentCd,
                  displayOptions: operatorProducts?.['response']?.displayOptions,
                  ga4Analytics,
                  afiMidvaleBillingAccountNumbers,
                  phoenixHorisonBillingAccountNumbers
                }))
              ];
            } else {
              rawPolicies = expPrds.map((exp: Record<string, unknown>) => ({
                ...exp,
                customerId: expProducts?.['response']?.customerId,
                ...metadata
              }));
            }

            return of(rawPolicies.map(raw => new PolicySummary(raw))).pipe(
              removeDuplicateAndExpiredPolicies(),
              map(policies => ({ policies, ...metadata, migrationBanner, policyAccessBanners }))
            );
          })
        );
      })
    );
  }

  private getHeaders(): HttpHeaders | null {
    if (this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)) return null;
    return new HttpHeaders()
      .set('afi-appname', env.afiAppName)
      .set('afi-api-key', env.afiApiKey)
      .set('feature-toggle', this.clPoliciesApiToggle);
  }

  public getPolicyDetails(policySummary: PolicySummary): Observable<Policy> {
    const policyWorker = this.getPolicyWorker(policySummary.generalizedProductType);
    if (!policyWorker) {
      return EMPTY;
    }

    const endpoint = policyWorker.getEndpoint();
    let workerUrl = `${endpoint}${policySummary.policyNumber}`;
    let params = new HttpParams();
    if (policySummary.generalizedProductType !== GenericProductType.Life) {
      params = params.set('sourceSystem', policySummary.sourcePath);
    }
    if (
      this.applicationService.isApp(Applications.MYACCOUNT_ADMIN) &&
      policySummary.customerMdmId
    ) {
      params = params.set('mdmId', policySummary.customerMdmId);
    }
    if (params.keys().length > 0) {
      workerUrl = workerUrl.concat(`?${params.toString()}`);
    }

    return policyWorker.getPolicy(workerUrl, policySummary).pipe(
      map(p => {
        if (p.policyNumber) {
          p.productDescription = policySummary.productDescription;
          p.sourceSystem = policySummary.sourceSystem;
          p.sourcePath = policySummary.sourcePath;
          p.policyStatus = Policy.getPolicyStatusType(p.status ? p.status : policySummary.status);
          p.billingAccountNumber = this.utilService.onlyAlphanumeric(p.billingAccountNumber);
          p.billingAccountNumberDisplay = this.utilService.onlyAlphanumeric(
            p.billingAccountNumberDisplay
          );
          p.policyPartnerId = policySummary.policyPartnerId;
          p.platform = policySummary.platform;
          return p;
        } else {
          return p;
        }
      })
    );
  }
  /*
    public getPolicy(product: Product): Observable<Policy> {
      const options = this.getOptions({});
      const policyWorker: TypedWorker = this.getPolicyWorker(product.generalizedProductType);
      if (policyWorker) {
        const endpoint = policyWorker.getEndpoint();
        const workerUrl = `${endpoint}${product.policyNumber}?sourceSystem=${product.sourcePath}`;
        return policyWorker.getPolicy(workerUrl, options).map(p => {
          p.status = product.status;
          p.sourceSystem = product.sourceSystemName;
          p.sourcePath = product.sourcePath;
          p.policyStatus = Policy.getPolicyStatusType(p.status);
          return p;
        });
      } else {
        return empty<Policy>();
      }
    }
  */

  public getPolicyWorker(type: GenericProductType | undefined): TypedWorker | null {
    let serviceInstance: TypedWorker | null = null;
    switch (type) {
      case GenericProductType.Auto:
        serviceInstance = this.plAutoService;
        break;
      case GenericProductType.Home:
        serviceInstance = this.plProperty;
        break;
      case GenericProductType.Umbrella:
        serviceInstance = this.plUmbrellaService;
        break;
      case GenericProductType.Life:
        serviceInstance = this.lifeService;
        break;
      default:
      // Return null for now to filter out other product types
      // throw new Error('Could not create policy worker of type: ' + type);
    }
    return serviceInstance;
  }
}
